import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Input } from '@/components';
import { PushDeeplinkField } from '@/components/admin/PushDeeplinkField';
import { formatInt } from '@/lib/format';
import { pushDataFromDeeplink } from '@/lib/push-deeplinks';
import { apiJson } from '@/lib/api';

type TestPushRes = { success: boolean; message: string; tokens_sent: number };

export type DeviceTestPushTarget = {
  installation_id: string;
  platform: string;
  nickname: string | null;
  unsubscribed_at: string | null;
  last_seen_at?: string;
};

function deviceLabel(device: DeviceTestPushTarget): string {
  const name = device.nickname ?? device.installation_id.slice(0, 8);
  return `${device.platform} · ${name}`;
}

export function DeviceTestPushPanel({
  devices,
}: {
  devices: DeviceTestPushTarget[];
}) {
  const sortedDevices = [...devices].sort((a, b) => {
    const aTime = a.last_seen_at ? Date.parse(a.last_seen_at) : 0;
    const bTime = b.last_seen_at ? Date.parse(b.last_seen_at) : 0;
    return bTime - aTime;
  });
  const [selectedInstallationId, setSelectedInstallationId] = useState(
    () => sortedDevices[0]?.installation_id ?? '',
  );
  const [title, setTitle] = useState('Test');
  const [body, setBody] = useState('Tap to open');
  const [deeplink, setDeeplink] = useState('');

  const selectedDevice =
    sortedDevices.find((device) => device.installation_id === selectedInstallationId) ??
    sortedDevices[0] ??
    null;

  const testPush = useMutation({
    mutationFn: (installationId: string) => {
      const data = pushDataFromDeeplink(deeplink);
      const payload: Record<string, unknown> = {
        installation_id: installationId,
        title,
        body,
      };
      if (data) payload.data = data;
      return apiJson<TestPushRes>('/v1/admin/send-test-push', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
  });

  if (sortedDevices.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        No linked devices to send a test push to.
      </p>
    );
  }

  const notificationsOff = Boolean(selectedDevice?.unsubscribed_at);
  const resultMessage = testPush.data
    ? `${testPush.data.success ? 'OK' : 'Not OK'}: ${testPush.data.message} (sent ${formatInt(testPush.data.tokens_sent)})`
    : undefined;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Send a one-off push to this device using its stored Expo token. Optionally choose a
        deeplink target to open when the user taps the notification.
      </p>

      {sortedDevices.length > 1 ? (
        <div>
          <label className="mb-1 block text-sm text-gray-500">Device</label>
          <select
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            value={selectedDevice?.installation_id ?? ''}
            onChange={(event) => setSelectedInstallationId(event.target.value)}
          >
            {sortedDevices.map((device) => (
              <option key={device.installation_id} value={device.installation_id}>
                {deviceLabel(device)}
                {device.unsubscribed_at ? ' (notifications off)' : ''}
              </option>
            ))}
          </select>
        </div>
      ) : selectedDevice ? (
        <p className="text-sm text-gray-600">
          Target: <span className="font-medium text-gray-900">{deviceLabel(selectedDevice)}</span>
        </p>
      ) : null}

      {notificationsOff ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          This device has notifications turned off, so Expo will not deliver a push.
        </p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Title" value={title} onChange={(event) => setTitle(event.target.value)} />
        <Input label="Body" value={body} onChange={(event) => setBody(event.target.value)} />
      </div>

      <PushDeeplinkField value={deeplink} onChange={setDeeplink} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {testPush.error || resultMessage ? (
          <p className={`text-sm ${testPush.error ? 'text-red-600' : 'text-gray-600'}`}>
            {testPush.error?.message ?? resultMessage}
          </p>
        ) : (
          <span />
        )}
        <Button
          className="sm:ml-auto"
          onClick={() => {
            if (!selectedDevice) return;
            void testPush.mutate(selectedDevice.installation_id);
          }}
          disabled={!selectedDevice}
          loading={testPush.isPending}
        >
          Send test push
        </Button>
      </div>
    </div>
  );
}
